// /////////////////////////////////////////////////////////////
// [ファイル] useSfaPipeline.ts
// [概要] 案件推進管理（推進残・受注残・失注分析）ダッシュボードのデータフック
//        断面集計・内訳集計・失注分析の取得と、断面の手動取得を管理する
// /////////////////////////////////////////////////////////////
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { format, subDays } from 'date-fns';
import { ep } from '../api/endpoints';

export interface PipelineSummaryRow {
  snapshot_date: string;
  aggregate_class: 'prospect' | 'pipeline' | 'backlog';
  rank_code: string | null;
  dim_code: string | null;
  dim_name: string | null;
  project_count: number;
  amount_sum: number;
  weighted_sum: number;
}

export interface PipelinePhaseMaster {
  phase: string;
  rank_code: string | null;
  probability: number;
  aggregate_class: string;
  sort_order: number;
}

export interface LostAnalysisRow {
  phase_before: string | null;
  rank_before: string;
  lost_reason_category: string;
  lost_reason_name: string;
  lost_count: number;
  lost_amount_sum: number;
}

export interface PipelineProjectRow {
  snapshot_date: string;
  project_id: string;
  project_name: string;
  project_type: string | null;
  phase: string;
  rank_code: string | null;
  probability: number | null;
  aggregate_class: string;
  amount: number | null;
  weighted_amount: number | null;
  expected_order_year: number | null;
  expected_order_month: number | null;
  order_confirmed_date: string | null;
  main_staff_user_id: string | null;
  main_staff_name: string | null;
  main_staff_department_name: string;
  main_staff_branch_name: string;
}

export interface PipelineFilters {
  fromDate: string;
  toDate: string;
  branch: string;      // branch_code。空文字は全支店
  staffId: string;     // 空文字は全担当者
  dimension: '' | 'branch' | 'department' | 'staff'; // 内訳軸。空文字は内訳なし
}

const defaultFilters = (): PipelineFilters => ({
  fromDate: format(subDays(new Date(), 180), 'yyyy-MM-dd'),
  toDate: format(new Date(), 'yyyy-MM-dd'),
  branch: '',
  staffId: '',
  dimension: '',
});

export function useSfaPipeline() {
  const [filters, setFilters] = useState<PipelineFilters>(defaultFilters);
  const [summary, setSummary] = useState<PipelineSummaryRow[]>([]);
  const [breakdown, setBreakdown] = useState<PipelineSummaryRow[]>([]);
  const [lostRows, setLostRows] = useState<LostAnalysisRow[]>([]);
  const [phases, setPhases] = useState<PipelinePhaseMaster[]>([]);
  const [loading, setLoading] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // フェーズ確度マスタ（ランク行の構成に使用）は初回のみ取得
  useEffect(() => {
    axios.post(ep('get_sfa_pipeline_masters'), {})
      .then((res) => {
        if (res.data.status === 'success') setPhases(res.data.phases || []);
      })
      .catch((err) => console.error('Error fetching pipeline masters:', err));
  }, []);

  const reload = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const base = {
        fromDate: filters.fromDate || null,
        toDate: filters.toDate || null,
        branch: filters.branch || null,
        staffId: filters.staffId || null,
      };
      const requests: Promise<any>[] = [
        axios.post(ep('get_sfa_pipeline_summary'), base),
        axios.post(ep('get_sfa_lost_analysis'), {
          fromDate: base.fromDate,
          toDate: base.toDate,
          branch: base.branch,
        }),
      ];
      if (filters.dimension) {
        requests.push(axios.post(ep('get_sfa_pipeline_summary'), { ...base, dimension: filters.dimension }));
      }
      const [summaryRes, lostRes, breakdownRes] = await Promise.all(requests);

      if (summaryRes.data.status === 'success') {
        setSummary(summaryRes.data.rows || []);
      } else {
        setError(summaryRes.data.error || '集計の取得に失敗しました');
      }
      if (lostRes.data.status === 'success') {
        setLostRows(lostRes.data.rows || []);
      }
      setBreakdown(breakdownRes?.data?.status === 'success' ? (breakdownRes.data.rows || []) : []);
    } catch (err: any) {
      console.error('Error loading pipeline summary:', err);
      setError(err.message || '通信エラーが発生しました');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    reload();
  }, [reload]);

  /** 断面の手動取得（当日日付で洗い替え）後、一覧を再読込する */
  const captureSnapshot = useCallback(async (): Promise<{ ok: boolean; message: string }> => {
    try {
      setCapturing(true);
      const res = await axios.post(ep('run_sfa_pipeline_snapshot'), {});
      if (res.data.status === 'success') {
        await reload();
        return { ok: true, message: `断面を取得しました（${res.data.snapshot_date} / ${res.data.captured_rows}件）` };
      }
      return { ok: false, message: res.data.error || '断面の取得に失敗しました' };
    } catch (err: any) {
      console.error('Error capturing snapshot:', err);
      return { ok: false, message: err.message || '通信エラーが発生しました' };
    } finally {
      setCapturing(false);
    }
  }, [reload]);

  /** セルのドリルダウン明細を取得する */
  const fetchProjects = useCallback(async (
    snapshotDate: string,
    aggregateClass?: string,
    rankCode?: string,
    dimFilter?: { branch?: string; department?: string; staffId?: string },
  ): Promise<PipelineProjectRow[]> => {
    const res = await axios.post(ep('get_sfa_pipeline_projects'), {
      snapshotDate,
      aggregateClass: aggregateClass || null,
      rankCode: rankCode || null,
      branch: dimFilter?.branch ?? (filters.branch || null),
      department: dimFilter?.department ?? null,
      staffId: dimFilter?.staffId ?? (filters.staffId || null),
    });
    if (res.data.status === 'success') return res.data.rows || [];
    throw new Error(res.data.error || '明細の取得に失敗しました');
  }, [filters.branch, filters.staffId]);

  return {
    filters,
    setFilters,
    summary,
    breakdown,
    lostRows,
    phases,
    loading,
    capturing,
    error,
    reload,
    captureSnapshot,
    fetchProjects,
  };
}
